// 이벤트 상세 페이지 — Hero/Actions / 참가자 / 팀 / 당일 순위 / 경기 섹션을
// 한 화면에 조립.
//
// 각 섹션은 이미 별도 모듈로 추출돼 있고, 이 모듈은 그것들을 위에서 아래로
// 쌓고 fetch + 폴링 시작만 담당. 시트 오픈/이벤트 라이프사이클/rerender는
// 모두 호출자(legacy.ts 또는 router)가 콜백으로 주입.

import { api } from "../api";
import { canManageEvent } from "../auth";
import { buildEventRankingSection, buildMatchCard } from "../matches/cards";
import { prefetchMembers } from "../state";
import { $ } from "../ui/dom";
import { surfaceLabel } from "../ui/format";
import { buildEventDetailHeader } from "./heroSection";
import { buildMatchCountCheck } from "./matchCountCheck";
import { buildMatchesSection } from "./matchesSection";
import { buildParticipantsSection } from "./participantsSection";
import { buildTeamsSection } from "./teamsSection";

export interface RenderEventDetailParams {
  view: HTMLElement;
  eventId: string;
  // sheet openers
  openEditEventSheet: (ev: any, courts: any[], participants: any[]) => void;
  openAddParticipantSheet: (eventId: string, participants: any[], ev?: any, canManage?: boolean) => void;
  openGuestLinkSheet: (ev: any) => void;
  openCompleteEventSheet: (ev: any, matches: any[]) => void;
  openParticipantAvailabilitySheet: (ev: any, p: any) => void;
  openGenerateScheduleSheet: (ev: any, regenerate: boolean) => void;
  openAddMatchSheet: (
    ev: any,
    participants: any[],
    courts: any[],
    existingBonusRounds: number[],
    opts?: { initialCourtId?: string | null; initialCourtLabel?: string | null },
  ) => void;
  openScoreSheet: (m: any, ev: any) => void;
  openSwapSheet: (m: any, pid: string, ev: any) => void;
  // lifecycle
  startEvent: (eventId: string, btn: HTMLButtonElement) => void;
  revertEventToReady: (eventId: string, btn: HTMLButtonElement) => void;
  // rerender + polling
  rerender: () => void;
  startPoll: (eventId: string, detail: any) => void;
}

export async function renderEventDetail(params: RenderEventDetailParams): Promise<void> {
  const {
    view, eventId,
    openEditEventSheet, openAddParticipantSheet, openGuestLinkSheet,
    openCompleteEventSheet, openParticipantAvailabilitySheet,
    openGenerateScheduleSheet, openAddMatchSheet,
    openScoreSheet, openSwapSheet,
    startEvent, revertEventToReady,
    rerender, startPoll,
  } = params;

  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let detail: any;
  try {
    detail = await api(`/api/v1/events/${eventId}`);
  } catch (e: any) {
    view.innerHTML = "";
    const err = document.createElement("div");
    err.className = "error-card";
    err.textContent = e.message;
    view.append(err);
    return;
  }

  const ev = detail.event;
  const participants = detail.participants || [];
  const matches = detail.matches || [];
  const teams = detail.teams || [];
  const rounds = detail.rounds || [];
  const courts = detail.courts || [];
  const roundMap = Object.fromEntries(rounds.map((r: any) => [r.round_number, r]));
  const surfaceMap = new Map<string, string>();
  for (const c of courts) {
    if (!c.surface_type) continue;
    const lbl = surfaceLabel(c.surface_type);
    if (lbl) surfaceMap.set(c.event_court_id, lbl);
  }

  if (canManageEvent(ev) && (ev.status === "draft" || ev.status === "ready" || ev.status === "in_progress")) {
    prefetchMembers();
  }

  const titleEl = $("#topbar-title");
  if (titleEl) titleEl.textContent = ev.title || "일정 정보";
  view.innerHTML = "";

  // Hero + actions
  for (const el of buildEventDetailHeader({
    ev, detail, matches, participants,
    canManage: canManageEvent(ev),
    onEdit: () => openEditEventSheet(ev, courts, participants),
    onAddParticipant: () => openAddParticipantSheet(ev.event_id, participants, ev, canManageEvent(ev)),
    onGuestLink: () => openGuestLinkSheet(ev),
    onStart: (btn) => startEvent(ev.event_id, btn),
    onRevert: (btn) => revertEventToReady(ev.event_id, btn),
    onComplete: () => openCompleteEventSheet(ev, matches),
  })) view.append(el);

  // Participants
  view.append(buildParticipantsSection({
    ev, participants, teams, matches, roundMap,
    canManage: canManageEvent(ev),
    openAvailability: (p) => openParticipantAvailabilitySheet(ev, p),
    openGuestLink: () => openGuestLinkSheet(ev),
  }));

  // 경기 수 점검 — 전 참가자 공개. 본인 경기 수 미리 보기 + 호스트에게
  // "왜 나만 적어?" 묻기 전에 분포 직접 확인 가능. 노출 데이터에 NTRP/연락처
  // 같은 민감 정보 없음.
  const countCheck = buildMatchCountCheck({ ev, matches, participants, teams });
  if (countCheck) view.append(countCheck);

  // Teams
  const teamsSec = buildTeamsSection({
    ev, teams, participants, matches, roundMap,
    canManage: canManageEvent(ev),
    hasMatches: matches.length > 0,
    onChanged: rerender,
  });
  if (teamsSec) view.append(teamsSec);

  // 당일 순위 (개인전/팀전만 — 정기모임은 숨김)
  if (ev.event_type === "special" && matches.length > 0) {
    const rankingSec = buildEventRankingSection(ev, matches, participants, teams);
    if (rankingSec) view.append(rankingSec);
  }

  // Matches
  const matchCard = (m: any) => buildMatchCard(m, ev, {
    surfaceMap,
    onScoreClick: () => openScoreSheet(m, ev),
    onPlayerSwap: (pid) => openSwapSheet(m, pid, ev),
  });
  view.append(buildMatchesSection({
    ev, matches, participants, courts, roundMap, surfaceMap,
    canManage: canManageEvent(ev),
    matchCard,
    openGenerate: (regen) => openGenerateScheduleSheet(ev, regen),
    openAddMatch: (existingBonusRounds, opts) =>
      openAddMatchSheet(ev, participants, courts, existingBonusRounds, opts),
  }));

  // Auto-poll for live events
  if (ev.status === "in_progress") startPoll(eventId, detail);
}
